import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { useAuth } from "../lib/auth.jsx";
import { getSupabase } from "../lib/supabase";
import { bookingDisplayCode } from "../lib/bookingCode.js";
import { bookingDesign, designImageUrl } from "../lib/designMedia.js";
import { CATS } from "../data/constants";
import { Modal, DesignDetailModal, BookingDetailModal } from "../components/DesignDetailModal";

const CURRENCY_SYMBOLS = {
  USD: "$", CAD: "CA$", GBP: "£", EUR: "€", NGN: "₦",
  GHS: "GH₵", ZAR: "R", KES: "KSh", AUD: "A$", NZD: "NZ$",
  AED: "د.إ", INR: "₹", SGD: "S$", MYR: "RM", JPY: "¥",
  BRL: "R$", MXN: "MX$", EGP: "E£", SAR: "﷼", ILS: "₪",
  CNY: "¥", KRW: "₩", THB: "฿", HKD: "HK$", IDR: "Rp",
};

function fmtCurrency(cents, currencyCode = "USD") {
  const code = String(currencyCode || "USD").toUpperCase();
  const amount = (Number(cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${CURRENCY_SYMBOLS[code] || code}${amount}`;
}

export function HistoryScreen() {
  const { authedFetch } = useAuth();
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [calendarAddedIds, setCalendarAddedIds] = useState(() => new Set(JSON.parse(localStorage.getItem("oge.calendarAdded") || "[]")));
  const [walletAddedIds, setWalletAddedIds] = useState(() => new Set(JSON.parse(localStorage.getItem("oge.walletAdded") || "[]")));
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("bookings");
  const [currencyCode, setCurrencyCode] = useState("USD");

  // Reschedule + review state
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [openSlots, setOpenSlots] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  // The past-design that's currently open in the details modal.
  const [designFor, setDesignFor] = useState(null);
  const [bookingFor, setBookingFor] = useState(null);
  const [favoriteFor, setFavoriteFor] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const [r, b, f, n, c] = await Promise.all([
        authedFetch("/api/designs/history"),
        authedFetch("/api/bookings/mine"),
        authedFetch("/api/favorites"),
        authedFetch("/api/notifications/me?limit=100"),
        authedFetch("/api/settings/currency").catch(() => null),
      ]);
      setReqs(r.requests); setBookings(b.bookings); setFavorites(f.favorites);
      setNotifications(n.notifications || []);
      if (c?.currency) setCurrencyCode(String(c.currency).toUpperCase());
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function startReschedule(b) {
    setErr("");
    try {
      const av = await authedFetch("/api/availability");
      setOpenSlots(av.slots);
      setRescheduleFor(b);
    } catch (e) { setErr(e.message); }
  }
  async function confirmReschedule(slot) {
    try {
      await authedFetch(`/api/bookings/${rescheduleFor.id}/reschedule`, {
        method: "POST", body: JSON.stringify({ newSlotId: slot.id }),
      });
      setRescheduleFor(null);
      refresh();
    } catch (e) { setErr(e.message); }
  }
  async function cancelBooking(b) {
    if (!confirm("Cancel this booking? Within 24h of the appointment a fee may apply.")) return;
    try {
      await authedFetch(`/api/bookings/${b.id}/cancel`, {
        method: "POST", body: JSON.stringify({ reason: "Customer cancelled via web" }),
      });
      refresh();
    } catch (e) { setErr(e.message); }
  }
  async function confirmBooking(b) {
    try {
      const out = await authedFetch(`/api/bookings/${b.id}/confirm`, { method: "POST" });
      if (out?.requiresDeposit) {
        const depositLabel = fmtCurrency(out.depositCents, currencyCode);
        if (!confirm(`A ${out.depositPercent}% deposit (${depositLabel}) is required. Have you sent it by bank transfer? The studio will verify it and confirm your appointment.`)) {
          refresh();
          return;
        }
        // Declare the deposit (transfer). The studio verifies it before the
        // booking is confirmed — it does NOT confirm immediately.
        await authedFetch(`/api/bookings/${b.id}/deposit`, {
          method: "POST",
          body: JSON.stringify({ method: "transfer" }),
        });
        alert(`Thanks! We've recorded your ${depositLabel} deposit. The studio will verify the transfer and confirm your appointment shortly.`);
      }
      refresh();
    } catch (e) { setErr(e.message); }
  }
  function downloadIcs(b) {
    // ICS endpoint requires the bearer token in the Authorization header. We
    // fetch it client-side and trigger a download as a Blob.
    authedFetch(`/api/bookings/${b.id}/ics`).then((text) => {
      const blob = new Blob([text], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `oge-${b.id.slice(0,8)}.ics`;
      a.click(); URL.revokeObjectURL(url);
      const next = new Set(calendarAddedIds);
      next.add(b.id);
      setCalendarAddedIds(next);
      localStorage.setItem("oge.calendarAdded", JSON.stringify([...next]));
    }).catch((e) => setErr(e.message));
  }

  async function addWalletPass(b) {
    setErr("");
    try {
      const sb = await getSupabase();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(`/api/wallet/booking/${b.id}.pkpass`, {
        headers: { Authorization: `Bearer ${session?.access_token || ""}` },
      });
      if (!res.ok) {
        const msg = res.headers.get("content-type")?.includes("application/json")
          ? (await res.json())?.error
          : await res.text();
        throw new Error(msg || "Wallet pass download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oge-booking-${bookingDisplayCode(b.id)}.pkpass`;
      a.click();
      URL.revokeObjectURL(url);
      const next = new Set(walletAddedIds);
      next.add(b.id);
      setWalletAddedIds(next);
      localStorage.setItem("oge.walletAdded", JSON.stringify([...next]));
    } catch (e) {
      setErr(e.message);
    }
  }

  function handleRebook(b) {
    const design = b.request?.final_design;
    if (!design || !b.request_id) return;
    sessionStorage.setItem("oge.reuse", JSON.stringify({ requestId: b.request_id, design }));
    navigate(`/book?reuse=${b.request_id}`);
  }

  async function markNotificationRead(n) {
    if (!n?.id || n.read_at) return;
    await authedFetch(`/api/notifications/${n.id}/read`, { method: "POST" });
    setNotifications((items) => items.map((item) => item.id === n.id ? { ...item, read_at: new Date().toISOString() } : item));
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "30px 16px" }}>
        <header style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
          gap: 16,
          alignItems: "end",
          marginBottom: 22,
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 1.4, color: "var(--accent)", textTransform: "uppercase" }}>
              Client workspace
            </div>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(34px, 6vw, 56px)", lineHeight: 1, margin: "8px 0 0", color: "var(--text)" }}>
              Your history
            </h1>
            <p style={{ margin: "10px 0 0", maxWidth: 560, color: "var(--text-muted)", fontSize: 14, lineHeight: 1.7 }}>
              Track bookings, revisit generated designs, and keep favorite looks ready for rebooking.
            </p>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(74px, 1fr))",
            gap: 8,
          }}>
            {[["Bookings", bookings.length], ["Designs", reqs.length], ["Saved", favorites.length]].map(([label, value]) => (
              <div key={label} style={{
                borderRadius: 16,
                border: "1px solid var(--border)",
                background: "var(--bg-elevated)",
                padding: "12px 14px",
                boxShadow: "var(--shadow-sm)",
              }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: "var(--text)" }}>{value}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
              </div>
            ))}
          </div>
        </header>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))",
          gap: 6,
          marginBottom: 20,
          padding: 5,
          borderRadius: 18,
          background: "var(--bg-elevated)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-sm)",
        }}>
          {[["bookings", `Bookings (${bookings.length})`], ["designs", `Designs (${reqs.length})`], ["favorites", `Favorites (${favorites.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: "11px 12px", borderRadius: 14, fontSize: 13, fontWeight: 800,
              background: tab === k ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-elevated)",
              color: tab === k ? "#fff" : "var(--text)",
              border: "none",
              cursor: "pointer",
            }}>{l}</button>
          ))}
        </div>

        {loading ? <Card><div style={{ textAlign: "center" }}>Loading…</div></Card> : (
          <>
            {tab === "bookings" && (
              bookings.length === 0 ? (
                <Card><div style={{ textAlign: "center", color: "var(--text-muted)" }}>No bookings yet.</div></Card>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {bookings.map((b) => (
                    <BookingRow key={b.id} booking={b}
                      careNotification={notifications.find((n) => n.kind === `care.${b.id}` || n.metadata?.bookingId === b.id)}
                      calendarAdded={calendarAddedIds.has(b.id)}
                      walletAdded={walletAddedIds.has(b.id)}
                      currencyCode={currencyCode}
                      onReschedule={() => startReschedule(b)}
                      onCancel={() => cancelBooking(b)}
                      onConfirm={() => confirmBooking(b)}
                      onIcs={() => downloadIcs(b)}
                      onWallet={() => addWalletPass(b)}
                      onReadNotification={markNotificationRead}
                      onOpen={() => setBookingFor(b)}
                      onReview={() => setReviewFor(b)}
                      onRebook={() => handleRebook(b)} />
                  ))}
                </div>
              )
            )}

            {tab === "designs" && (
              reqs.length === 0 ? (
                <Card><div style={{ textAlign: "center", color: "var(--text-muted)" }}>No designs yet.</div></Card>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
                  {reqs.filter(r => r.final_design || (r.generated && r.generated.length > 0)).map((r) => (
                    <PastDesignCard key={r.id} req={r} onOpen={() => setDesignFor(r)} />
                  ))}
                </div>
              )
            )}

            {tab === "favorites" && (
              favorites.length === 0 ? (
                <Card><div style={{ textAlign: "center", color: "var(--text-muted)" }}>No favorites yet — tap the heart on any design to save it.</div></Card>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
                  {favorites.map((f) => <FavoriteCard key={f.id} fav={f} onOpen={() => setFavoriteFor(f)} onRemove={async () => {
                    await authedFetch(`/api/favorites/${f.id}`, { method: "DELETE" });
                    refresh();
                  }} />)}
                </div>
              )
            )}
          </>
        )}

        {err && <Card style={{ marginTop: 14 }}><div role="alert" style={{ color: "#C02020" }}>{err}</div></Card>}

        {rescheduleFor && (
          <Modal title="Pick a new time" onClose={() => setRescheduleFor(null)}>
            {openSlots.length === 0 ? (
              <p style={{ color: "var(--text-muted)" }}>No open slots in the next 14 days.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
                {openSlots.map((s) => (
                  <button key={s.id} onClick={() => confirmReschedule(s)} style={{
                    background: "var(--bg-elevated)", border: "1.5px solid var(--border)",
                    borderRadius: 12, padding: "10px 12px", cursor: "pointer", textAlign: "left",
                  }}>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                      {new Date(s.starts_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
                      {new Date(s.starts_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Modal>
        )}

        {reviewFor && (
          <ReviewModal booking={reviewFor} onClose={() => setReviewFor(null)} onDone={() => { setReviewFor(null); refresh(); }} />
        )}

        {designFor && (
          <DesignDetailModal req={designFor} onClose={() => setDesignFor(null)} />
        )}
        {bookingFor && (
          <BookingDetailModal
            booking={bookingFor}
            onClose={() => setBookingFor(null)}
            calendarAdded={calendarAddedIds.has(bookingFor.id)}
            walletAdded={walletAddedIds.has(bookingFor.id)}
            onReschedule={() => startReschedule(bookingFor)}
            onCancel={() => cancelBooking(bookingFor)}
            onIcs={() => downloadIcs(bookingFor)}
            onWallet={() => addWalletPass(bookingFor)}
            onReview={() => setReviewFor(bookingFor)}
            onRebook={() => { setBookingFor(null); handleRebook(bookingFor); }}
          />
        )}
        {favoriteFor && (
          <FavoriteDetailModal
            favorite={favoriteFor}
            onClose={() => setFavoriteFor(null)}
            onRemove={async () => {
              await authedFetch(`/api/favorites/${favoriteFor.id}`, { method: "DELETE" });
              setFavoriteFor(null);
              refresh();
            }}
          />
        )}
      </div>
    </div>
  );
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  ));

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener?.(onChange);
    return () => {
      mql.removeListener?.(onChange);
    };
  }, [query]);

  return matches;
}

function BookingRow({ booking: b, careNotification, calendarAdded, walletAdded, currencyCode, onReschedule, onCancel, onConfirm, onIcs, onWallet, onReadNotification, onOpen, onReview, onRebook }) {
  const [showCare, setShowCare] = useState(false);
  const compact = useMediaQuery("(max-width: 560px)");
  const design = bookingDesign(b);
  const imageUrl = designImageUrl(design);
  const awaitingConfirmation = b.status === "pending_customer_confirmation";
  const canReschedule = ["pending", "confirmed", "rescheduled"].includes(b.status);
  const canReview     = b.status === "completed";
  const startsAt = b.slot ? new Date(b.slot.starts_at) : null;
  const inFuture = startsAt && startsAt.getTime() > Date.now();
  const completed = ["completed", "complete"].includes(String(b.status).toLowerCase());
  const canAddCalendar = Boolean(startsAt && inFuture && !completed && !calendarAdded);
  const canAddWallet = Boolean(!completed && !walletAdded);
  function plainNotificationText(value = "") {
    const doc = new DOMParser().parseFromString(String(value), "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return (
    <article style={{
      background: "var(--bg-elevated)",
      borderRadius: compact ? 16 : 20,
      padding: compact ? 14 : 18,
      border: "1px solid var(--border)",
      boxShadow: "var(--shadow-sm)",
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: imageUrl
          ? (compact ? "72px minmax(0, 1fr)" : "84px 54px minmax(0, 1fr) auto")
          : "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
        gap: compact ? 12 : 14,
        alignItems: "flex-start",
      }}>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={design.name || "Nail design"}
            style={{
              width: compact ? 72 : 84,
              height: compact ? 72 : 84,
              borderRadius: compact ? 14 : 16,
              objectFit: "cover",
              border: "1px solid var(--border)",
              background: "var(--bg-soft)",
            }}
          />
        )}
        <div style={{
          width: 54,
          height: 54,
          borderRadius: 18,
          background: "linear-gradient(135deg, rgba(196,135,154,0.20), rgba(201,168,76,0.16))",
          border: "1px solid var(--border)",
          display: "grid",
          placeItems: "center",
          color: "var(--accent)",
          fontWeight: 900,
          fontSize: 12,
          display: compact ? "none" : "grid",
        }}>
          {startsAt ? startsAt.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "Soon"}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
            {design.name || "Booking"}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-accent)", marginTop: 3, fontWeight: 800, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            Booking #{bookingDisplayCode(b.id)}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 9 }}>
            <span style={bookingMetaPill()}>{startsAt ? startsAt.toLocaleString() : "Slot pending"}</span>
            <span style={bookingMetaPill()}>{fmtCurrency(b.price_cents, b.currency || currencyCode)}</span>
            {(b.tip_cents > 0) && <span style={bookingMetaPill()}>Tip {fmtCurrency(b.tip_cents, b.currency || currencyCode)}</span>}
            {completed && b.updated_at && <span style={bookingMetaPill()}>Completed {new Date(b.updated_at).toLocaleDateString()}</span>}
          </div>
        </div>
        <span style={{
          fontSize: 11,
          fontWeight: 800,
          padding: "6px 12px",
          borderRadius: 999,
          background: badgeBg(b.status),
          color: badgeFg(b.status),
          whiteSpace: "normal",
          justifySelf: compact ? "start" : "end",
          gridColumn: compact ? "1 / -1" : undefined,
        }}>
          {formatStatus(b.status)}
        </span>
      </div>
      {awaitingConfirmation && (
        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 12, background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.3)", fontSize: 13, color: "var(--text)" }}>
          The studio prepared this appointment for you at <strong>{fmtCurrency(b.price_cents, b.currency || currencyCode)}</strong>. Confirm to lock in your slot{b.confirmation_expires_at ? ` (offer expires ${new Date(b.confirmation_expires_at).toLocaleString()})` : ""}.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <Button size="sm" variant="outline" label="Details" onClick={onOpen} />
        {awaitingConfirmation && <Button size="sm" variant="gold" label="Confirm appointment" onClick={onConfirm} />}
        {awaitingConfirmation && <Button size="sm" variant="danger" label="Decline" onClick={onCancel} />}
        {inFuture && canReschedule && <Button size="sm" variant="outline" label="Reschedule" onClick={onReschedule} />}
        {inFuture && canReschedule && <Button size="sm" variant="danger"  label="Cancel"     onClick={onCancel} />}
        <Button size="sm" variant="ghost" label={calendarAdded ? "Calendar added" : "Add to Calendar"} onClick={onIcs} disabled={!canAddCalendar} />
        <Button size="sm" variant="outline" label={walletAdded ? "Wallet added" : "Add to Wallet"} onClick={onWallet} disabled={!canAddWallet} />
        {canReview && <Button size="sm" variant="gold" label="Leave review" onClick={onReview} />}
        {completed && b.request?.final_design && onRebook && (
          <Button size="sm" variant="primary" label="Rebook" onClick={onRebook} />
        )}
      </div>
      {completed && careNotification?.body && (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <button
            type="button"
            onClick={() => {
              setShowCare(!showCare);
              if (!showCare) onReadNotification?.(careNotification);
            }}
            style={{
              width: "100%",
              border: "1px solid var(--border)",
              background: "var(--bg-soft)",
              color: "var(--text)",
              borderRadius: 12,
              padding: "10px 12px",
              textAlign: "left",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            {careNotification.read_at ? "Aftercare message" : "Unread aftercare message"} · {showCare ? "Hide" : "View"}
          </button>
          {showCare && (
            <div style={{ marginTop: 8, color: "var(--text-muted)", fontSize: 13, lineHeight: 1.7 }}>
              {plainNotificationText(careNotification.body)}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function bookingMetaPill() {
  return {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    borderRadius: 999,
    padding: "5px 10px",
    background: "var(--bg-soft)",
    border: "1px solid var(--border)",
    color: "var(--text-muted)",
    fontSize: 12,
    fontWeight: 700,
  };
}

function PastDesignCard({ req: r, onOpen }) {
  const cat = CATS.find(c => c.id === r.category);
  const img = r.final_design?.image_url || r.generated?.[0]?.image_url;
  const designName = r.final_design?.name || "Not finalised";
  const finalised  = Boolean(r.final_design);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View details for ${designName}`}
      style={{
        background: "var(--bg-elevated)",
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid var(--border)",
        padding: 0,
        textAlign: "left",
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        transition: "transform .18s var(--ease-out), box-shadow .18s var(--ease-out), border-color .18s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 12px 28px rgba(196,135,154,0.18)";
        e.currentTarget.style.borderColor = "var(--border-strong)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "var(--border)";
      }}
    >
      {img && <img src={img} alt="" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block" }} />}
      <div style={{ padding: 12 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>{cat?.icon} {cat?.label || r.category}</div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--text)", marginTop: 4 }}>
          {designName}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{new Date(r.created_at).toLocaleDateString()}</span>
          <span aria-hidden="true" style={{ color: "var(--accent)", fontWeight: 700 }}>View →</span>
        </div>
        {!finalised && (
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>
            {r.generated?.length || 0} option{r.generated?.length === 1 ? "" : "s"} generated
          </div>
        )}
      </div>
    </button>
  );
}

function FavoriteCard({ fav, onOpen, onRemove }) {
  const d = fav.design_snapshot || {};
  return (
    <article onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); }
    }} style={{ background: "var(--bg-elevated)", borderRadius: 14, overflow: "hidden", border: "1px solid var(--border)", cursor: "pointer" }}>
      {d.image_url && <img src={d.image_url} alt="" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block" }} />}
      <div style={{ padding: 12 }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
          {d.name || "Saved design"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{d.tagline}</div>
        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="sm" variant="outline" label="Details" onClick={(e) => { e.stopPropagation(); onOpen(); }} />
          <Button size="sm" variant="danger" label="Remove" onClick={(e) => { e.stopPropagation(); onRemove(); }} />
        </div>
      </div>
    </article>
  );
}

function FavoriteDetailModal({ favorite, onClose, onRemove }) {
  const d = favorite.design_snapshot || {};
  return (
    <Modal title={d.name || "Favorite design"} onClose={onClose} maxWidth={720}>
      <div style={{ display: "grid", gap: 14 }}>
        {d.image_url && <img src={d.image_url} alt="" style={{ width: "100%", maxHeight: 430, objectFit: "cover", borderRadius: 16, border: "1px solid var(--border)" }} />}
        <div>
          <div style={{ color: "var(--text-accent)", fontWeight: 800, fontSize: 13 }}>{d.tagline}</div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.7, margin: "8px 0 0" }}>{d.description}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
          {[["Shape", d.nailShape], ["Length", d.nailLength], ["Finish", d.finish], ["Time", d.duration], ["Price", d.priceRange], ["Difficulty", d.difficulty]].map(([label, value]) => (
            <div key={label} style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", borderRadius: 12, padding: "10px 12px" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase" }}>{label}</div>
              <div style={{ color: "var(--text)", fontWeight: 700, marginTop: 3 }}>{value || "—"}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button label="Remove Favorite" variant="danger" onClick={onRemove} />
          <Button label="Close" variant="outline" onClick={onClose} />
        </div>
      </div>
    </Modal>
  );
}

function ReviewModal({ booking, onClose, onDone }) {
  const { authedFetch } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit() {
    setBusy(true); setErr("");
    try {
      await authedFetch(`/api/bookings/${booking.id}/reviews`, {
        method: "POST", body: JSON.stringify({ rating, comment }),
      });
      onDone();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  return (
    <Modal title="Leave a review" onClose={onClose}>
      <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 16 }}>
        {[1,2,3,4,5].map((n) => (
          <button key={n} onClick={() => setRating(n)} aria-label={`${n} star${n > 1 ? 's' : ''}`}
            style={{ background: "none", border: "none", fontSize: 28, cursor: "pointer",
              color: n <= rating ? "var(--gold)" : "var(--text-muted)" }}>
            ★
          </button>
        ))}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)}
        placeholder="What did you love? (optional)" rows={4}
        style={{
          width: "100%", padding: 12, borderRadius: 10, fontSize: 14,
          border: "1.5px solid var(--border-strong)", background: "var(--bg-elevated)", color: "var(--text)",
          fontFamily: "inherit", resize: "vertical",
        }} />
      {err && <div role="alert" style={{ color: "#C02020", fontSize: 13, marginTop: 8 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <Button label="Cancel" variant="outline" onClick={onClose} />
        <Button label="Submit review" variant="primary" onClick={submit} loading={busy} />
      </div>
    </Modal>
  );
}

function formatStatus(raw = "") {
  const value = String(raw).toLowerCase();
  if (value === "in_progress") return "In Progress";
  if (value === "no_show" || value === "no-show") return "No Show";
  if (value === "pending_customer_confirmation") return "Awaiting your confirmation";
  if (value === "payment_pending") return "Deposit — awaiting verification";
  return String(raw).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function badgeBg(s) { return ({ pending:"rgba(201,168,76,0.16)", pending_customer_confirmation:"rgba(201,168,76,0.2)", payment_pending:"rgba(201,168,76,0.2)", confirmed:"rgba(50,160,80,0.16)", cancelled:"rgba(180,50,50,0.12)", completed:"rgba(50,160,80,0.16)", complete:"rgba(50,160,80,0.16)", rescheduled:"rgba(196,135,154,0.16)", in_progress:"rgba(196,135,154,0.18)", no_show:"rgba(180,50,50,0.12)" }[s]) || "rgba(196,135,154,0.16)"; }
function badgeFg(s) { return ({ pending:"#6A4800", pending_customer_confirmation:"#6A4800", payment_pending:"#6A4800", confirmed:"#1F6E36", cancelled:"#8B2222", completed:"#1F6E36", complete:"#1F6E36", rescheduled:"#5A2A38", in_progress:"#5A2A38", no_show:"#8B2222" }[s]) || "#5A2A38"; }
